/* Mobile Active: shared helpers for model search, mega menu and model switcher. */
(() => {
  const MA = (window.MA = window.MA || {});

  MA.esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  MA.norm = (s) => String(s || '').toLowerCase().replace(/galaxy /g, '').replace(/[^a-z0-9]/g, '');
  MA.handleOf = (url) => {
    const m = String(url || '').match(/\/collections\/([^/?#]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  };
  MA.splitTitle = (title) => {
    const subs = [];
    const name = String(title)
      .replace(/\(([^()]*)\)/g, (m, g) => {
        subs.push(g.replace(/\s*\/\s*/g, ' · ').trim());
        return ' ';
      })
      .replace(/\s+/g, ' ')
      .trim();
    return { name, sub: subs.filter(Boolean).join(' · ') };
  };

  /* ---------- model index (from the main menu) ---------- */
  MA.index = () => {
    if (MA._idx) return MA._idx;
    let data = { brands: [] };
    const el = document.getElementById('ma-model-index');
    try {
      if (el) data = JSON.parse(el.textContent);
    } catch (e) {
      console.warn('Model index could not be read', e);
    }
    // Menu titles ending in " NEW" get a badge; the word itself is removed.
    const NEW_RE = /\s+NEW$/;
    (data.brands || []).forEach((b) => {
      b.t = String(b.t || '').replace(NEW_RE, '');
      (b.s || []).forEach((s) => {
        s.t = String(s.t || '').replace(NEW_RE, '');
        (s.m || []).forEach((pair) => {
          const t = String(pair[0] || '').trim();
          pair[2] = NEW_RE.test(t);
          pair[0] = t.replace(NEW_RE, '');
        });
      });
    });
    const models = [];
    data.brands.forEach((b, bi) => {
      (b.s || []).forEach((s, si) => {
        (s.m || []).forEach((pair, mi) => {
          const p = MA.splitTitle(pair[0]);
          models.push({
            title: pair[0], name: p.name, sub: p.sub, url: pair[1], handle: MA.handleOf(pair[1]), isNew: !!pair[2],
            brand: b.t, brandUrl: b.u, series: s.t, seriesUrl: s.u, bi, si, mi,
            nameKey: MA.norm(p.name), key: MA.norm(pair[0] + ' ' + s.t)
          });
        });
      });
    });
    MA._idx = { data, models };
    return MA._idx;
  };

  MA.findByHandle = (handle) => MA.index().models.find((m) => m.handle && m.handle === handle) || null;

  MA.searchModels = (query, limit = 8, onlyCollections = false) => {
    const tokens = String(query || '').toLowerCase().split(/\s+/).map(MA.norm).filter(Boolean);
    if (!tokens.length) return [];
    const full = tokens.join('');
    const hits = [];
    MA.index().models.forEach((m, i) => {
      if (onlyCollections && !m.handle) return;
      if (!tokens.every((t) => m.key.indexOf(t) >= 0)) return;
      const score = m.nameKey.indexOf(full) === 0 ? 0 : m.nameKey.indexOf(full) >= 0 ? 1 : 2;
      hits.push({ m, score, i });
    });
    hits.sort((a, b) => a.score - b.score || a.i - b.i);
    return hits.slice(0, limit).map((h) => h.m);
  };

  /* ---------- related models for the switcher ---------- */
  MA.family = (name) => {
    let n = String(name).replace(/^Galaxy /i, '').trim();
    const drop = /\s+(Pro Max|Pro|Plus|Ultra|Mini|Max|Lite|FE|5G|4G|XL|Edge|Neo|Fusion|Core|Nacho|Refine|Active|e)$/i;
    let prev;
    do {
      prev = n;
      n = n.replace(drop, '');
    } while (n !== prev);
    return n.length >= 2 ? n.toLowerCase() : null;
  };

  MA.siblings = (model) => {
    if (!model) return [];
    const fam = MA.family(model.name);
    if (!fam) return [];
    const list = MA.index().models.filter((m) => m.bi === model.bi && m.si === model.si && MA.family(m.name) === fam).reverse();
    if (list.length < 2 || list.length > 6) return [];
    const names = list.map((m) => m.name.replace(/^Galaxy /i, ''));
    return list.map((m, i) => {
      const dup = names.filter((n) => n === names[i]).length > 1;
      return { model: m, label: names[i] + (dup && m.sub ? ' · ' + m.sub : '') };
    });
  };

  MA.renderSwitcher = (host, model, makeHref) => {
    const sibs = MA.siblings(model);
    if (!sibs.length) {
      host.hidden = true;
      host.innerHTML = '';
      return;
    }
    host.hidden = false;
    host.innerHTML =
      '<span class="ma-switch__label">Not the right one? Switch model</span><div class="ma-switch__group">' +
      sibs
        .map((s) => {
          const on = s.model === model;
          return '<a class="ma-switch__btn' + (on ? ' is-current' : '') + '" href="' + MA.esc(makeHref(s.model)) + '"' + (on ? ' aria-current="page"' : '') + ' data-ma-handle="' + MA.esc(s.model.handle || '') + '">' + MA.esc(s.label) + '</a>';
        })
        .join('') +
      '</div>';
  };

  /* ---------- <ma-model-search> ---------- */
  class MaModelSearch extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      this.mode = this.dataset.mode || 'navigate';
      this.form = this.querySelector('form');
      this.input = this.querySelector('.ma-search__input');
      this.list = this.querySelector('.ma-search__list');
      this.active = -1;
      this.items = [];
      this.input.addEventListener('input', () => this.update());
      this.input.addEventListener('focus', () => this.update());
      this.input.addEventListener('keydown', (e) => this.onKey(e));
      this.input.addEventListener('blur', () => setTimeout(() => this.close(), 150));
      this.list.addEventListener('mousedown', (e) => e.preventDefault());
      this.list.addEventListener('click', (e) => {
        const li = e.target.closest('[data-i]');
        if (li) {
          e.preventDefault();
          this.choose(Number(li.dataset.i));
        }
      });
      this.form.addEventListener('submit', (e) => {
        if (this.mode === 'select') {
          e.preventDefault();
          if (this.items.length && this.items[0].model) this.choose(0);
        }
      });
    }

    update() {
      const q = this.input.value.trim();
      const models = q.length >= 2 ? MA.searchModels(q, 8, this.mode === 'select') : [];
      this.items = models.map((m) => ({ model: m }));
      if (this.mode !== 'select' && q.length >= 2) this.items.push({ all: q });
      this.active = -1;
      if (!this.items.length) {
        if (q.length >= 2 && this.mode === 'select') {
          this.list.innerHTML = '<li class="ma-search__none" role="presentation">No model matches “' + MA.esc(q) + '”. Try the model code, like A526.</li>';
          this.open();
        } else this.close();
        return;
      }
      const id = this.list.id;
      this.list.innerHTML = this.items
        .map((it, i) => {
          if (it.all) {
            return '<li id="' + id + '-' + i + '" class="ma-search__opt ma-search__opt--all" role="option" data-i="' + i + '">Search all products for “' + MA.esc(it.all) + '”</li>';
          }
          const m = it.model;
          return (
            '<li id="' + id + '-' + i + '" class="ma-search__opt" role="option" data-i="' + i + '">' +
            '<span class="ma-search__name">' + MA.esc(m.name) + (m.isNew ? ' <span class="ma-new">NEW</span>' : '') + '</span>' +
            '<span class="ma-search__where">' + MA.esc([m.brand, m.series, m.sub].filter(Boolean).join(' · ')) + '</span></li>'
          );
        })
        .join('');
      this.open();
    }

    open() {
      this.list.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.list.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }

    setActive(i) {
      const opts = this.list.querySelectorAll('[data-i]');
      if (!opts.length) return;
      this.active = (i + opts.length) % opts.length;
      opts.forEach((o, k) => o.classList.toggle('is-active', k === this.active));
      this.input.setAttribute('aria-activedescendant', opts[this.active].id);
      opts[this.active].scrollIntoView({ block: 'nearest' });
    }

    onKey(e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (this.list.hidden) this.update();
        this.setActive(this.active + 1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.setActive(this.active - 1);
      } else if (e.key === 'Enter' && this.active >= 0) {
        e.preventDefault();
        this.choose(this.active);
      } else if (e.key === 'Escape') {
        this.close();
      }
    }

    choose(i) {
      const it = this.items[i];
      if (!it) return;
      if (it.all) {
        this.form.submit();
        return;
      }
      if (this.mode === 'select') {
        this.dispatchEvent(new CustomEvent('ma:model-select', { bubbles: true, detail: it.model }));
        this.input.value = '';
        this.close();
        return;
      }
      window.location.href = it.model.url;
    }
  }
  if (!customElements.get('ma-model-search')) customElements.define('ma-model-search', MaModelSearch);

  /* ---------- full-width mega menu ----------
     Two layouts per menu (Customize > Header > "Menus that open in overview"):
       sidebar  - series list on the left, one series' models on the right; hover switches series.
       overview - every series side by side with its first few models; click a series to open all of it.
     Clicking a series pins it: hovering other series no longer switches. Clicking it again,
     clicking outside the panel, "All series", or closing the menu releases the pin.
     Model order: "rows" (across) or "columns" (down first, up to about 16 per column). */
  const COL_TARGET_ROWS = 10; // aim for about this many models per column (column order)
  const COL_MIN_PX = 190;   // narrowest model column

  const store = {
    get: (k) => { try { return localStorage.getItem(k) || ''; } catch (e) { return ''; } },
    set: (k, v) => { try { v ? localStorage.setItem(k, v) : localStorage.removeItem(k); } catch (e) {} }
  };

  const megas = () => [...document.querySelectorAll('[data-ma-mega]')];
  const isSearching = (mega) => {
    const f = mega.querySelector('.ma-mega__filter');
    return !!(f && f.value.trim().length >= 2);
  };

  const setActive = (mega, idx) => {
    mega.querySelectorAll('.ma-mega__series-btn').forEach((b) => {
      const on = idx !== null && b.dataset.maSeries === String(idx);
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
  };
  const activeIdx = (mega) => {
    const b = mega.querySelector('.ma-mega__series-btn.is-active');
    return b ? b.dataset.maSeries : null;
  };

  // Column order: fill a column top to bottom, then the next one.
  const layoutFlow = (mega) => {
    const cols = mega.dataset.maFlow === 'columns';
    mega.querySelectorAll('[data-ma-panel]:not([hidden]) .ma-mega__models').forEach((list) => {
      if (!cols) {
        list.querySelectorAll('.ma-col-fill').forEach((f) => f.remove());
        list.style.gridTemplateRows = '';
        list.style.gridTemplateColumns = '';
        return;
      }
      list.querySelectorAll('.ma-col-fill').forEach((f) => f.remove());
      const items = [...list.children].filter((li) => !li.classList.contains('ma-mega__gen-line'));
      const width0 = list.clientWidth || mega.clientWidth || 1000;
      const fit0 = Math.max(1, Math.floor(width0 / COL_MIN_PX));
      // Groups (Z Flip / Z Fold, or a few generations) that fit side by side get a column each.
      const sizes = [];
      items.forEach((li, i) => { if (i === 0 || li.classList.contains('ma-gen-start')) sizes.push(0); sizes[sizes.length - 1]++; });
      const tallest = Math.max(...sizes);
      if (sizes.length >= 2 && sizes.length <= fit0 && tallest <= 16) {
        let at = 0;
        sizes.forEach((size) => {
          at += size;
          const after = items[at - 1];
          for (let k = size; k < tallest; k++) {
            const f = document.createElement('li');
            f.className = 'ma-col-fill';
            f.setAttribute('aria-hidden', 'true');
            after.after(f);
          }
        });
        items.forEach((li) => li.classList.toggle('ma-col-top', li === items[0] || li.classList.contains('ma-gen-start')));
        list.style.gridTemplateRows = 'repeat(' + tallest + ', auto)';
        list.style.gridTemplateColumns = 'repeat(' + sizes.length + ', minmax(' + COL_MIN_PX / 10 + 'rem, 30rem))';
        return;
      }
      const n = items.length;
      const width = list.clientWidth || mega.clientWidth || 1000;
      const fit = Math.max(1, Math.floor(width / COL_MIN_PX));
      // Balanced: spread over the columns that fit, so no long column next to a short one.
      // Short lists stay in one column; longer ones split into even columns of ~10,
      // and only grow taller than that when no more columns fit.
      const used = Math.max(1, Math.min(fit, Math.ceil(n / COL_TARGET_ROWS)));
      const rows = Math.max(1, Math.ceil(n / used));
      // No generation line on the first model of a column (it would sit on the panel edge).
      items.forEach((li, i) => li.classList.toggle('ma-col-top', i % rows === 0));
      list.style.gridTemplateRows = 'repeat(' + rows + ', auto)';
      // keep the usual column width; spare columns stay empty on the right
      // Columns keep a readable width (not stretched across the panel).
      list.style.gridTemplateColumns = 'repeat(' + used + ', minmax(' + COL_MIN_PX / 10 + 'rem, 30rem))';
    });
  };

  // The theme closes the menu the moment focus leaves it. If the element the customer just
  // clicked gets hidden (overview -> series, "All series" -> overview), the browser drops focus
  // to the page and the menu closes. So focus is moved to something still inside the menu.
  const keepFocus = (mega) => {
    const a = document.activeElement;
    if (!a || !mega.contains(a) || a.offsetParent !== null) return;
    const target = mega.querySelector('.ma-mega__series-btn.is-active') || mega.closest('.mega-menu__content') || mega;
    if (target === mega && !mega.hasAttribute('tabindex')) mega.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });
  };

  // Decides what the right-hand side shows: search results, the overview, or one series.
  const megaRender = (mega) => {
    if (!mega) return;
    megaShow(mega);
    keepFocus(mega);
  };
  const megaShow = (mega) => {
    const ov = mega.querySelector('[data-ma-ov]');
    const results = mega.querySelector('[data-ma-results]');
    const panels = mega.querySelectorAll('[data-ma-panel]');
    const pinned = mega.hasAttribute('data-ma-pinned');
    if (isSearching(mega)) {
      if (ov) ov.hidden = true;
      panels.forEach((p) => { p.hidden = true; });
      if (results) results.hidden = false;
      return;
    }
    if (results) results.hidden = true;
    if (mega.dataset.maView === 'overview' && !pinned) {
      setActive(mega, null);
      if (ov) ov.hidden = false;
      panels.forEach((p) => { p.hidden = true; });
      return;
    }
    if (ov) ov.hidden = true;
    let idx = activeIdx(mega);
    if (idx === null) {
      idx = '0';
      setActive(mega, idx);
    }
    panels.forEach((p) => { p.hidden = p.dataset.maPanel !== idx; });
    layoutFlow(mega);
  };

  const megaUnpin = (mega) => {
    if (!mega || !mega.hasAttribute('data-ma-pinned')) return;
    mega.removeAttribute('data-ma-pinned');
    mega.querySelectorAll('.ma-mega__series-btn.is-pinned').forEach((b) => b.classList.remove('is-pinned'));
    megaRender(mega);
  };
  const megaOpen = (mega, idx, pin) => {
    const f = mega.querySelector('.ma-mega__filter');
    if (f && f.value) {
      f.value = '';
      const box = mega.querySelector('[data-ma-results]');
      if (box) box.innerHTML = '';
    }
    setActive(mega, idx);
    if (pin) {
      mega.setAttribute('data-ma-pinned', '');
      mega.querySelectorAll('.ma-mega__series-btn').forEach((b) => b.classList.toggle('is-pinned', b.dataset.maSeries === String(idx)));
    }
    megaRender(mega);
  };

  const SKIP_BRANDS = /^(tools|accessories)$/i;

  const megaFilter = (input) => {
    const mega = input.closest('[data-ma-mega]');
    if (!mega) return;
    const box = mega.querySelector('[data-ma-results]');
    const q = input.value.trim();
    if (q.length < 2) {
      box.innerHTML = '';
      megaRender(mega);
      return;
    }
    const hits = MA.searchModels(q, 200).filter((m) => !SKIP_BRANDS.test(m.brand.trim()));
    megaRender(mega);
    if (!hits.length) {
      box.innerHTML = '<p class="ma-mega__empty">No model matches “' + MA.esc(q) + '”. Try the model code printed on the device, like A526 or XT2415.</p>';
      return;
    }
    const groups = [];
    const byKey = {};
    hits.forEach((m) => {
      const k = m.bi + ':' + m.si;
      if (!byKey[k]) {
        byKey[k] = { brand: m.brand, series: m.series, items: [] };
        groups.push(byKey[k]);
      }
      byKey[k].items.push(m);
    });
    box.innerHTML =
      '<div class="ma-mega__panel-head"><span class="ma-mega__panel-title">Results for “' + MA.esc(q) + '”</span></div>' +
      '<div class="ma-mega__result-groups">' +
      groups.map((g) =>
        '<section class="ma-mega__group"><h3 class="ma-mega__group-title"><span>' + MA.esc(g.brand) + '</span><span class="ma-mega__group-sep" aria-hidden="true">›</span><span>' + MA.esc(g.series) + '</span></h3>' +
        '<ul class="ma-mega__models ma-mega__models--results" role="list">' +
        g.items.map((m) => '<li><a class="ma-mega__model" href="' + MA.esc(m.url) + '"><span class="ma-mega__model-name">' + MA.esc(m.name) + (m.isNew ? ' <span class="ma-new">NEW</span>' : '') + '</span><span class="ma-mega__model-sub">' + MA.esc(m.sub) + '</span></a></li>').join('') +
        '</ul></section>'
      ).join('') +
      '</div>';
  };

  const onHover = (e) => {
    const b = e.target.closest && e.target.closest('.ma-mega__series-btn');
    if (!b) return;
    const mega = b.closest('[data-ma-mega]');
    if (!mega || mega.hasAttribute('data-ma-pinned') || isSearching(mega)) return;
    if (mega.dataset.maView === 'overview') return; // overview: click to open a series
    if (!b.classList.contains('is-active')) megaOpen(mega, b.dataset.maSeries, false);
  };
  document.addEventListener('mouseover', onHover);
  document.addEventListener('focusin', onHover);

  // Keep focus inside the menu when its buttons are clicked: Safari and Firefox on Mac don't focus
  // buttons on click, and the theme closes the menu as soon as focus leaves it.
  document.addEventListener('mousedown', (e) => {
    const b = e.target.closest && e.target.closest('[data-ma-mega] button, [data-ma-mega] [data-ma-open]');
    if (b && document.activeElement !== b) b.focus({ preventScroll: true });
  });
  // Listens on window in the capture phase so it runs before any other script on the page
  // (theme, apps) can stop the click or react to it first.
  window.addEventListener('click', (e) => {
    const t = e.target;
    // Links like "#0" inside the menu are placeholders: never jump the page for them.
    const hashLink = t.closest && t.closest('[data-ma-mega] a[href^="#"]');
    if (hashLink) e.preventDefault();
    // Any click outside a menu panel releases its pin (page, other top menu items).
    megas().forEach((mega) => {
      if (mega.hasAttribute('data-ma-pinned') && !mega.contains(t)) megaUnpin(mega);
    });
    // Overview series name / "All N models": do exactly what clicking that series on the left does.
    const opener = t.closest && t.closest('[data-ma-open]');
    if (opener) {
      e.preventDefault();
      e.stopPropagation();
      const mega = opener.closest('[data-ma-mega]');
      const left = mega && mega.querySelector('.ma-mega__series-btn[data-ma-series="' + opener.dataset.maOpen + '"]');
      if (left) {
        left.focus({ preventScroll: true }); // focus stays inside the menu, so it doesn't close
        left.click();
      }
      return;
    }
    const b = t.closest && t.closest('.ma-mega__series-btn');
    if (b) {
      e.preventDefault();
      const mega = b.closest('[data-ma-mega]');
      const idx = b.dataset.maSeries || b.dataset.maOpen;
      if (b.classList.contains('is-pinned')) megaUnpin(mega); // click the pinned one again = release
      else megaOpen(mega, idx, true);
      return;
    }
    const back = t.closest && t.closest('[data-ma-back]');
    if (back) {
      e.preventDefault();
      const mega = back.closest('[data-ma-mega]');
      const content = mega.closest('.mega-menu__content');
      if (content) content.focus({ preventScroll: true });
      megaUnpin(mega);
    }
  }, true);

  // Dim the page a little while any top menu panel is open.
  const dimSync = () => {
    let dim = document.querySelector('.ma-dim');
    if (!dim) {
      dim = document.createElement('div');
      dim.className = 'ma-dim';
      dim.setAttribute('aria-hidden', 'true');
      document.body.appendChild(dim);
    }
    dim.classList.toggle('is-on', !!document.querySelector('.header__inline-menu details[open]'));
  };
  document.addEventListener('toggle', (e) => {
    if (e.target && e.target.tagName === 'DETAILS' && e.target.closest('.header__inline-menu')) dimSync();
  }, true);

  // Opening a menu lays out the columns; closing it releases the pin.
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (!d || d.tagName !== 'DETAILS') return;
    d.querySelectorAll('[data-ma-mega]').forEach((mega) => {
      if (!d.open) megaUnpin(mega);
      else megaRender(mega);
    });
  }, true);
  let rz;
  window.addEventListener('resize', () => {
    clearTimeout(rz);
    rz = setTimeout(() => megas().forEach(layoutFlow), 120);
  });

  /* ---------- generation groups inside a series (iPhone 18 / 17 / 16 ...) ----------
     Worked out from the model names, so new models group themselves:
     "iPhone 19 Pro Max" goes under "iPhone 19 series" with no menu changes.
     The key is the name up to its first number: iPhone 17 Pro -> iPhone 17,
     Galaxy S25+ -> Galaxy S25, Pixel 9a -> Pixel 9, Apple Watch Series 10 -> Apple Watch Series 10.
     Single models with no partner (iPhone Air, iPhone XR) stay with the group above them.
     Row layout: each new generation starts a new row with a line above it.
     Column layout: a line above the first model of each generation.
     Panels where most models are singles (Galaxy A, Moto) are left as a plain grid. */
  const genOf = (name) => {
    const words = String(name).replace(/\s+/g, ' ').trim().split(' ');
    const i = words.findIndex((w) => /\d/.test(w) && !/^[2-5]G$/i.test(w)); // 5G / 4G are not model numbers
    let label;
    if (i >= 0) {
      const m = words[i].match(/^[A-Za-z]*\d+(?:\.\d+)?/);
      label = words.slice(0, i).concat(m ? m[0] : words[i]).join(' ');
    } else {
      const fam = MA.family(name);
      if (!fam) return null;
      label = words.slice(0, fam.split(' ').length + (/^Galaxy /i.test(name) ? 1 : 0)).join(' ');
    }
    return { key: label.toLowerCase(), label };
  };

  const MIN_GROUPS = 2;      // need at least this many real groups (2+ models each)
  const MAX_SINGLES = 0.25;  // and no more than this share of single models

  // Family = the name without its number: "Z Flip 7 5G" -> "z flip", "Z FOLD 8 Ultra" -> "z fold".
  const familyOf = (name) => {
    const words = String(name).replace(/\s+/g, ' ').trim().split(' ');
    const i = words.findIndex((w) => /\d/.test(w) && !/^[2-5]G$/i.test(w));
    if (i > 0) return { key: words.slice(0, i).join(' ').toLowerCase() };
    const fam = MA.family(name);
    return fam ? { key: fam } : null;
  };

  const buildGroups = (items, keyOf) => {
    const groups = [];
    items.forEach(({ li, name }) => {
      const g = keyOf(name);
      const last = groups[groups.length - 1];
      if (g && last && last.key === g.key) last.items.push(li);
      else groups.push({ key: g ? g.key : '', items: [li] });
    });
    const real = groups.filter((g) => g.items.length > 1).length;
    const singles = groups.filter((g) => g.items.length === 1).length;
    if (real < MIN_GROUPS || singles > Math.max(1, items.length * MAX_SINGLES)) return null;
    // Singles join the group above them (or below, if they come first).
    const merged = [];
    groups.forEach((g) => {
      if (g.items.length === 1 && merged.length) merged[merged.length - 1].items.push(...g.items);
      else merged.push(g);
    });
    if (merged.length > 1 && merged[0].items.length === 1) {
      merged[1].items.unshift(...merged[0].items);
      merged.shift();
    }
    return merged.length >= MIN_GROUPS ? merged : null;
  };

  const groupPanel = (list) => {
    if (list.dataset.maGrouped) return;
    list.dataset.maGrouped = '1';
    const items = [...list.children]
      .filter((li) => !li.hasAttribute('data-ma-extra') && li.querySelector('.ma-mega__model-name'))
      .map((li) => {
        const nameEl = li.querySelector('.ma-mega__model-name');
        return { li, name: (nameEl.firstChild && nameEl.firstChild.nodeType === 3 ? nameEl.firstChild.textContent : nameEl.textContent).trim() };
      });
    if (items.length < 6) return;
    // First try generations (iPhone 17 / 16 ...). If the list is ordered by family instead
    // (all Z Flips, then all Z Folds), split by family.
    const merged = buildGroups(items, genOf) || buildGroups(items, familyOf);
    if (!merged) return;
    list.classList.add('is-grouped');
    merged.forEach((g, i) => {
      if (i === 0) return;
      g.items[0].classList.add('ma-gen-start'); // used by the column layout
      const line = document.createElement('li');
      line.className = 'ma-mega__gen-line';
      line.setAttribute('role', 'presentation');
      line.setAttribute('aria-hidden', 'true');
      list.insertBefore(line, g.items[0]);
    });
  };


  const initMega = () => {
    document.querySelectorAll('[data-ma-mega] [data-ma-panel] .ma-mega__models').forEach(groupPanel);
    // Layout and model order come only from the theme settings (Customize > Header > Parts menu).
    megas().forEach((mega) => {
      mega.dataset.maView = mega.dataset.maDefaultView || 'sidebar';
      mega.dataset.maFlow = mega.dataset.maDefaultFlow || 'columns';
      megaRender(mega);
    });
    // Clear anything the old test panel saved in this browser.
    ['ma_test', 'ma_test_view', 'ma_test_flow'].forEach((k) => store.set(k, ''));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initMega);
  else initMega();

  document.addEventListener('input', (e) => {
    if (e.target.matches && e.target.matches('.ma-mega__filter')) megaFilter(e.target);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && e.target.matches && e.target.matches('.ma-mega__filter') && e.target.value) {
      e.stopPropagation();
      e.target.value = '';
      megaFilter(e.target);
    }
  }, true);

  /* ---------- model switcher on collection / product pages ---------- */
  const initSwitchers = () => {
    document.querySelectorAll('[data-ma-switcher]').forEach((host) => {
      const model = MA.findByHandle(host.dataset.handle);
      MA.renderSwitcher(host, model, (m) => m.url);
    });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initSwitchers);
  else initSwitchers();
})();
